import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PropertyValuationData } from "../../types/valuation";
import { fetchPropertyPhotos } from "../../api/propertyPhotos";

function formatAddress(
  address?: PropertyValuationData["propertyInfo"]["address"]
): string | null {
  if (!address?.address || !address?.city || !address?.state || !address?.zip) {
    return null;
  }
  return `${address.address}, ${address.city}, ${address.state} ${address.zip}`;
}

function isValidUrl(urlString: string): boolean {
  try {
    new URL(urlString);
    return true;
  } catch {
    return false;
  }
}

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

function getStreetViewUrl(
  property: Omit<PropertyValuationData, "id"> & { id: string | number }
): string | null {
  const formattedAddress = formatAddress(property.propertyInfo?.address);
  if (formattedAddress) {
    return `https://maps.googleapis.com/maps/api/streetview?size=600x400&location=${encodeURIComponent(
      formattedAddress
    )}&key=${GOOGLE_MAPS_API_KEY}`;
  }

  const { latitude, longitude } = property.propertyInfo || {};
  if (latitude && longitude) {
    return `https://maps.googleapis.com/maps/api/streetview?size=600x400&location=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`;
  }

  return null;
}

async function checkStreetViewAvailability(url: string): Promise<boolean> {
  try {
    const metadataUrl = url.replace("streetview?", "streetview/metadata?");
    const response = await fetch(metadataUrl);
    const data = await response.json();
    return data.status === "OK";
  } catch (error) {
    console.error("Error checking Street View availability:", error);
    return false;
  }
}

// Base64 encoded placeholder SVG
const PLACEHOLDER_IMAGE = "/assets/images/place-holder.webp";

export interface PropertyPhoto {
  type: "valuation" | "streetview" | "placeholder";
  primaryUrl: string;
  additionalUrls?: string[];
  photoCount?: number;
  metadata?: {
    status: string;
    location?: { lat: number; lng: number };
    date?: string;
    copyright?: string;
  };
}

async function fetchPropertyPhotosWithFallback(
  property: Omit<PropertyValuationData, "id"> & { id: string | number }
): Promise<PropertyPhoto> {
  // Try valuation photos API first
  const formattedAddress = formatAddress(property.propertyInfo?.address);
  if (formattedAddress) {
    try {
      const valuationPhotos = await fetchPropertyPhotos(formattedAddress);
      if (valuationPhotos.photoCount > 0) {
        return {
          type: "valuation",
          primaryUrl: valuationPhotos.primaryPhoto!,
          additionalUrls: valuationPhotos.additionalPhotos?.filter(isValidUrl),
          photoCount: valuationPhotos.photoCount,
        };
      }
    } catch (error) {
      console.warn("Failed to fetch from valuation photos API:", error);
    }
  }

  // Try Street View
  const streetViewUrl = getStreetViewUrl(property);
  if (streetViewUrl) {
    const hasStreetView = await checkStreetViewAvailability(streetViewUrl);
    if (hasStreetView) {
      return {
        type: "streetview",
        primaryUrl: streetViewUrl,
        metadata: {
          status: "OK",
          location: {
            lat: property.propertyInfo?.latitude || 0,
            lng: property.propertyInfo?.longitude || 0,
          },
        },
      };
    }
  }

  // Use placeholder as last resort
  return {
    type: "placeholder",
    primaryUrl: PLACEHOLDER_IMAGE,
  };
}

export function usePropertyPhotosQuery(
  property: Omit<PropertyValuationData, "id"> & { id: string | number }
) {
  const queryClient = useQueryClient();

  const query = useQuery<PropertyPhoto, Error>({
    queryKey: ["propertyPhotos", property.id],
    queryFn: () => fetchPropertyPhotosWithFallback(property),
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
  });

  // Prefetch images when data is available
  const prefetchImages = (urls: string[]) => {
    urls.forEach((url) => {
      const img = new Image();
      img.src = url;
    });
  };

  // When data is successfully fetched, prefetch all images
  if (
    query.data &&
    query.data.type === "valuation" &&
    query.data.additionalUrls
  ) {
    const allUrls = [
      query.data.primaryUrl,
      ...query.data.additionalUrls,
    ].filter(Boolean);
    prefetchImages(allUrls);
  }

  return {
    ...query,
    mainPhotos: query.data,
  };
}
